import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useMemo, type FormEvent } from 'react';
import { catalogEvents } from '@bb/contracts';
import { Button, ErrorState, ReadOnlyBanner } from '@bb/ui';
import { ApiError, type EventDetails, type EventProducer, type EventStatus } from '../api/client.ts';
import { usePermissions } from '../features/access/usePermissions.ts';
import { EditorShell } from '../features/editor/EditorShell.tsx';
import { useEditorState } from '../features/editor/useEditorState.ts';
import { useObjectTab } from '../features/tabs/objectTabs.ts';
import { EVENT_STATUS_LABEL } from './EventsPage.tsx';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

type Draft = { description: string; owner: string; producer: EventProducer };
const draftOf = (e: EventDetails): Draft => ({ description: e.description ?? '', owner: e.owner ?? '', producer: e.producer });

/** One custom event (BB-27): description, owner and producer with an unsaved-changes guard, status, properties, example. */
export function EventPage() {
  const { environmentId, name } = useParams({ from: '/env/$environmentId/data/events/$name' });
  const { api } = useRouteContext({ from: '/env/$environmentId/data/events/$name' });
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canEdit = has('catalog.edit');
  const key = ['event', environmentId, name];
  const event = useQuery({ queryKey: key, queryFn: () => api.getEvent(environmentId, name) });
  const server = useMemo(() => (event.data ? draftOf(event.data) : undefined), [event.data]);
  const editor = useEditorState(server);
  const draft = editor.draft;
  const dirty = editor.isDirty;
  useObjectTab(environmentId, event.data ? { type: 'event', id: name, title: name, url: `/env/${environmentId}/data/events/${encodeURIComponent(name)}`, listUrl: `/env/${environmentId}/data/events` } : null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: ['events', environmentId] });
  };
  const save = useMutation({
    mutationFn: (d: Draft) => api.updateEvent(environmentId, name, { description: d.description.trim() || null, owner: d.owner.trim() || null, producer: d.producer }),
    onSuccess: async (_result, d) => {
      editor.markSaved({ description: d.description.trim(), owner: d.owner.trim(), producer: d.producer });
      await refresh();
    },
  });
  const status = useMutation({ mutationFn: (next: EventStatus) => api.updateEvent(environmentId, name, { status: next }), onSuccess: refresh });
  const segmentable = useMutation({ mutationFn: ({ path, on }: { path: string; on: boolean }) => api.updateEventProperty(environmentId, name, path, { segmentable: on }), onSuccess: refresh });

  if (event.isPending || !draft) return event.isError ? <ErrorState title="The event could not be loaded" message={errorText(event.error)} /> : <p aria-busy="true">Loading event…</p>;
  const e = event.data!;
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (dirty) save.mutate(draft);
  };

  return (
    <EditorShell dirty={dirty} onSave={() => save.mutateAsync(draft)} objectLabel="event">
    <section>
      <h1>{e.name}</h1>
      <p>
        <span className="tag tag--neutral">{EVENT_STATUS_LABEL[e.status]}</span> {e.lastSeenAt ? `last seen ${e.lastSeenAt.slice(0, 10)}` : 'not seen yet'}
      </p>
      {!canEdit && <ReadOnlyBanner>Changing the registry needs the catalog.edit permission.</ReadOnlyBanner>}
      {e.notices.map((n) => (
        <p key={n.id} className="notice notice--warning" role="status">
          {n.kind === 'unknown_event' ? `Seen ${n.count} times before anyone described it.` : `${n.kind.replaceAll('_', ' ')}: ${n.subject} (${n.count})`}
        </p>
      ))}

      <form aria-label="Event details" onSubmit={submit} className="stack-form">
        <label>
          Description
          <textarea value={draft.description} disabled={!canEdit} onChange={(ev) => editor.update({ description: ev.target.value })} />
        </label>
        <label>
          Owner
          <input value={draft.owner} disabled={!canEdit} onChange={(ev) => editor.update({ owner: ev.target.value })} />
        </label>
        <label>
          Producer
          <select value={draft.producer} disabled={!canEdit} onChange={(ev) => editor.update({ producer: ev.target.value as EventProducer })}>
            {(['sdk', 'server', 'both', 'unknown'] as const).map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        {canEdit && (
          <Button type="submit" disabled={!dirty || save.isPending}>
            Save
          </Button>
        )}
        {dirty && <span className="hint">Unsaved changes</span>}
        {save.isError && <span role="alert">{errorText(save.error)}</span>}
      </form>

      {canEdit && catalogEvents.EVENT_STATUS_TRANSITIONS[e.status].length > 0 && (
        <div className="filters" role="group" aria-label="Change status">
          {catalogEvents.EVENT_STATUS_TRANSITIONS[e.status].map((next) => (
            <Button key={next} onClick={() => status.mutate(next)} disabled={status.isPending} variant={next === 'blocked' ? 'danger' : 'secondary'}>
              Mark {EVENT_STATUS_LABEL[next].toLowerCase()}
            </Button>
          ))}
          {status.isError && <span role="alert">{errorText(status.error)}</span>}
        </div>
      )}

      <h2>Properties</h2>
      {e.properties.length === 0 ? (
        <p className="hint">No properties seen yet.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Path</th>
              <th scope="col">Type</th>
              <th scope="col">Sensitivity</th>
              <th scope="col">Use in segments</th>
            </tr>
          </thead>
          <tbody>
            {e.properties.map((p) => (
              <tr key={p.path}>
                <td>
                  <code>{p.path}</code>
                </td>
                <td>{p.dataType}</td>
                <td>{p.sensitivity === 'none' ? '—' : p.sensitivity === 'secret' ? 'Secret (never stored)' : 'PII'}</td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Use ${p.path} in segments`}
                    checked={Boolean(p.segmentable)}
                    disabled={!canEdit || p.sensitivity === 'secret' || segmentable.isPending}
                    onChange={(ev) => segmentable.mutate({ path: p.path, on: ev.target.checked })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {segmentable.isError && <p role="alert">{errorText(segmentable.error)}</p>}

      <h2>Example payload</h2>
      {e.examplePayload ? <pre aria-label="Example payload">{JSON.stringify(e.examplePayload, null, 2)}</pre> : <p className="hint">No example yet.</p>}

      <h2>Aliases</h2>
      {e.aliases?.length ? (
        <p>
          Also received as: {e.aliases.map((a) => <code key={a}>{a} </code>)}
        </p>
      ) : (
        <p className="hint">No old spellings.</p>
      )}

      <h2>Used in</h2>
      {e.usage.length === 0 ? (
        <p className="hint">Nothing uses this event yet.</p>
      ) : (
        <ul>
          {e.usage.map((u) => (
            <li key={`${u.objectType}:${u.objectId}`}>
              {u.name} ({u.objectType})
            </li>
          ))}
        </ul>
      )}
    </section>
    </EditorShell>
  );
}
